import { property, subclass } from '@arcgis/core/core/accessorSupport/decorators';
import { messageBundle, tsx } from '@arcgis/core/widgets/support/widget';
import Widget from '@arcgis/core/widgets/Widget';

import { VNode } from 'src/interfaces/interfaces';
import PublishAppViewModel, { AGOL_USER_ROOT_FOLDER_KEY, RedirectOption, UserFolder } from '../data/PublishApp/PublishAppViewModel';
import { navbarRoute } from '../ui/NavBar/NavBarViewModel';
import { AppTypeInfo } from './AppTypeTile';

import "./PublishForm/styles/PublishForm.scss";

/** 
 * Different UIs the PublishForm can be launched as 
 * "positionedOverlay" - implies that this is being rendered in a Preview Modal - ** important for telemetry - if this changes, consider necessary updates to telemetry
*/
export type PublishUI = "modal" | "positionedOverlay";
/** Different Modes the PublishForm can use to create apps */
export type PublishMode = "newApp" | "copyApp" | "fromExistingApp";

export interface IPublishFormProperties extends __esri.WidgetProperties {
  ui: PublishUI;
  mode: PublishMode;
  appTypeInfo: AppTypeInfo;

  /** Required when mode==="newApp" */
  webmapItem?: __esri.PortalItem;
  /** Required when mode==="copyApp" || mode ==="fromExistingApp" */
  existingAppItem?: __esri.PortalItem;

  publishAppVM: PublishAppViewModel;

  updateFunction?: Function;
  feedbackFunction?: (msg: string, success: boolean) => void;
  sendFocusOnClose: Function;
}

@subclass('Landing.widgets.PublishForm')
export default class PublishForm extends Widget {

  @property()
  ui: PublishUI = "modal";

  @property()
  mode: PublishMode;

  @property()
  appTypeInfo: AppTypeInfo;

  @property()
  currentNavRoute: navbarRoute;

  @property()
  webmapItem: __esri.PortalItem;

  @property()
  existingAppItem: __esri.PortalItem;

  @property()
  publishAppVM: PublishAppViewModel;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages;

  @property()
  updateFunction?: Function;

  @property()
  feedbackFunction?: (msg: string, success: boolean, appId?: string) => void;

  @property()
  sendFocusOnClose: Function;

  @property()
  publishResultMessage: string;

  @property()
  publishResultMessageType: "success" | "error";

  private _appTitle: string;
  private _appTags: string[] = [];
  private _folderId: string = AGOL_USER_ROOT_FOLDER_KEY;

  private _isFormValid: boolean = false;
  private _isTitleValid: boolean = null;
  /** Tags and Folder is always valid */
  private _isTagsValid: boolean = true;
  private _isFolderValid: boolean = true;

  private _titleInvalidMsg: string;
  private _tagsInvalidMsg: string;

  private _currentExtraVal: string;
  private _comboboxGrandChildren: HTMLCollection;

  constructor(properties?: IPublishFormProperties) {
    super(properties);
  }

  postInitialize() {

  }

  render(): VNode {
    const form = this._renderForm();
    return this.ui === "positionedOverlay"
      ? this._renderPositionedOverlay(form)
      : this._renderModal(form);
  }

  private _renderForm(): VNode {

    return (
      <div class="publish-form__form">
        { this._renderAppTitleInput()}
        { this._renderTagsInput()}
        { this._renderFolderInput()}
      </div>
    )
  }

  private _renderPositionedOverlay(form: VNode) {
    return (
      <div class="publish-form">
        <focus-trap>
          {this._renderHeader()}
          {form}
          {this._renderOverlayBtns()}
        </focus-trap>
      </div>
    )
  }

  private _renderHeader(): VNode {
    return (
      <div class="publish-form__header">
        <h3>{this.appMessages.globalNav.title} - {this.appTypeInfo.appTypeTitle}</h3>

        <calcite-button
          icon-start="x"
          scale="l"
          color="neutral"
          appearance="transparent"
          onclick={() => { this._destroyPublishOverlay() }}
        ></calcite-button>
      </div>
    )
  }

  private _renderOverlayBtns(): VNode[] {
    const { publishForm } = this.appMessages;

    let primaryBtnClickFunc: Function;
    let secondaryBtnClickFunc: Function;
    let primaryBtnLabel: string = publishForm.createBtnMsg;
    let secondaryBtnLabel: string = publishForm.publish;

    let showSettingsUpdateWarning: boolean = false;

    if (this.mode === "newApp") {
      primaryBtnClickFunc = this._publishNewApp.bind(this, "goToConfigure");
      secondaryBtnClickFunc = this._publishNewApp.bind(this, "goToItemDetails");
    } else if (this.mode === "copyApp") {
      // No need for copyApp in the IFramePreviewModal
    } else if (this.mode === "fromExistingApp") {
      primaryBtnClickFunc = this._publishNewAppFromExisting.bind(this, "goToConfigure");
      secondaryBtnClickFunc = this._publishNewAppFromExisting.bind(this, "goToItemDetails");
      showSettingsUpdateWarning = true;
    }
    return ([
      showSettingsUpdateWarning ? (
        <div>{publishForm.settingsUpdateWarning}</div>
      ) : null,
      <div class="publish-form__buttons">
        <div>
          <calcite-button
            width="full"
            appearance="outline"
            onclick={secondaryBtnClickFunc}
            disabled={!this._isFormValid || this.publishAppVM.isPublishing}
            loading={this.publishAppVM.isPublishing && this.publishAppVM.redirectionTarget === "goToItemDetails"}
          >
            {secondaryBtnLabel}
          </calcite-button>
        </div>
        <div>
          <calcite-button
            width="full"
            onclick={primaryBtnClickFunc}
            disabled={!this._isFormValid || this.publishAppVM.isPublishing}
            loading={this.publishAppVM.isPublishing && this.publishAppVM.redirectionTarget === "goToConfigure"}
          >
            {primaryBtnLabel}
          </calcite-button>
        </div>
      </div>

    ])
  }

  private _renderModal(form: VNode): VNode {
    const { publishForm, modals, globalNav } = this.appMessages;
    let primaryBtnClickFunc: Function;
    let primaryBtnLabel: string;
    let showSettingsUpdateWarning: boolean = false;

    if (this.mode === "newApp") {
      primaryBtnClickFunc = this._publishNewApp.bind(this, "goToConfigure");
      primaryBtnLabel = publishForm.createBtnMsg;
    } else if (this.mode === "copyApp") {
      primaryBtnClickFunc = this._copyApp.bind(this);
      primaryBtnLabel = publishForm.copyApp;
    } else if (this.mode === "fromExistingApp") {
      primaryBtnClickFunc = this._publishNewAppFromExisting.bind(this, "goToConfigure");
      primaryBtnLabel = publishForm.createBtnMsg;
      showSettingsUpdateWarning = true;
    }
    return (
      <calcite-modal
        class="publish-form-modal"
        active="true"
        background-color="grey"
        afterCreate={(modal) => { this._handleModalClose(modal) }}
        intl-close={modals.closeButtonTip}
      >
        <div slot="header">{globalNav.title} - {this.appTypeInfo.appTypeTitle}</div>
        <div slot="content">
          {form}
        </div>
        { showSettingsUpdateWarning && (
          <div slot="back" class="settings-update-warning">
            <p>
              {publishForm.settingsUpdateWarning}
            </p>
          </div>
        )}
        <calcite-button
          slot="secondary"
          width="full"
          appearance="outline"
          onclick={() => { this._destroyPublishOverlay() }}
        >
          {publishForm.close}</calcite-button>
        <calcite-button
          onclick={primaryBtnClickFunc}
          slot="primary"
          disabled={!this._isFormValid || this.publishAppVM.isPublishing}
          loading={this.publishAppVM.isPublishing}
          width="full">
          {primaryBtnLabel}
        </calcite-button>
      </calcite-modal>
    )
  }


  private _handleModalClose(modal: HTMLCalciteModalElement): void {
    modal.addEventListener("calciteModalClose", () => {
      this._destroyPublishOverlay();
    });
  }

  private _renderAppTitleInput(): VNode {
    const { title, titlePlaceHolder, copyFormKeyword } = this.appMessages.publishForm;

    return (
      <calcite-label
        status={this._isTitleValid === false ? "invalid" : "valid"}
      >
        {title}
        <calcite-input
          required={true}
          status={this._isTitleValid === false ? "invalid" : "valid"}
          placeholder={titlePlaceHolder}
          value={
            this.mode === "copyApp"
              ? `${this.existingAppItem.title} - ${copyFormKeyword}`
              : null
          }
          afterCreate={(input: HTMLCalciteInputElement) => {
            input.addEventListener("calciteInputBlur", this._handleAppTitleValidation.bind(this));
            if (input.value != null && input.value !== "") {
              this._handleAppTitleValidation({ target: { value: input.value } });
            }
            // // Title input should always have focus upon first render of the form
            setTimeout(() => { input.setFocus(); }, 500);
          }}
        ></calcite-input>
        <calcite-input-message active={this._isTitleValid === false} status="invalid" icon="exclamation-mark-triangle" >
          {this._titleInvalidMsg}
        </calcite-input-message>
      </calcite-label>
    )
  }

  private _publishNewApp(redirection: RedirectOption): void {
    this.publishAppVM.createApp({
      redirect: redirection,
      title: this._appTitle,
      folderId: this._folderId,
      tags: this._formatAGOLTags(),
      webmapItem: this.webmapItem,
      appTypeInfo: this.appTypeInfo
    },
      this._publishTelemetryCall.bind(this)
    ).then(() => {
      this._destroyPublishOverlay();
    });
  }

  private _copyApp(): void {
    const destroyFunc = this._destroyPublishOverlay.bind(this);
    this.publishAppVM.copyApp({
      newTitle: this._appTitle,
      newTags: this._formatAGOLTags(),
      folder: this._folderId,
      appToCopyId: this.existingAppItem.id,
      appTypeInfo: this.appTypeInfo
    },
      this._publishTelemetryCall.bind(this)
    ).then((resultMsg: [string, string]) => {
      this?.feedbackFunction(resultMsg[0], true, resultMsg[1]);
      destroyFunc();
    }).catch((err) => {
      this?.feedbackFunction(err, false);
      destroyFunc();
      console.error("copy err", err);
    });
  }

  private async _publishNewAppFromExisting(redirection: RedirectOption): Promise<void> {
    try {
      await this.publishAppVM.createAppFromExisting(
        {
          redirect: redirection,
          title: this._appTitle,
          folderId: this._folderId,
          tags: this._formatAGOLTags(),
          webmapItem: this.webmapItem,
          appTypeInfo: this.appTypeInfo
        },
        this.existingAppItem.id,
        this._publishTelemetryCall.bind(this)
      );
    } catch (err) {
      console.error(err);
    }
    this._destroyPublishOverlay();
  }

  private _publishTelemetryCall() {
    this.publishAppVM.telemetry.logEvent({
      category: "Instant Apps Gallery",
      action: `Publish - ${this.publishAppVM.navbarVM.navigationRoute}`,
      label: this.appTypeInfo.appTypeUrlFragment,
      details: this.mode,
      attribute: this.ui === "modal" ? "Create Modal" : "Preview Modal",
    });
  }

  private _determineFormValidity(): void {
    this._isFormValid = this._isTitleValid && this._isTagsValid && this._isFolderValid;
  }

  private async _handleAppTitleValidation(blurEvent): Promise<void> {
    const { publishForm } = this.appMessages;
    const titleVal: string = blurEvent.target.value;
    this._appTitle = titleVal;

    if (titleVal === "") {
      this._isTitleValid = false;
      this._titleInvalidMsg = publishForm.titleValidationEmpty;
      this._determineFormValidity();
      this.scheduleRender(); // required here - ui refresh doesn't work without it
    } else if (await this.publishAppVM.doesTitleExist(titleVal, this._folderId)) {
      this._showInvalidTitleAlreadyExists(titleVal);
    } else {
      this._showValidTitle();
    }
  }

  private _renderTagsInput() {
    const { publishForm } = this.appMessages;

    const startingTags = this.mode === "newApp" || this.mode === "fromExistingApp"
      ? this._renderTags(this?.webmapItem?.tags)
      : this._renderTags(this?.existingAppItem?.tags);

    return (
      <calcite-label
      >
        {publishForm.tags}
        <calcite-combobox
          label={publishForm.tags}
          placeholder={publishForm.tags}
          selection-mode="multi"
          scale="m"
          allow-custom-values="true"
          max-items="0"
          afterCreate={(combobox: HTMLCalciteComboboxElement) => {
            this._comboboxHandler(combobox)
          }}
        >
          {startingTags}
        </calcite-combobox>

        <calcite-input-message active={this._isTagsValid === false} status="invalid" icon="exclamation-mark-triangle" >
          {this._tagsInvalidMsg}
        </calcite-input-message>
      </calcite-label>
    );
  }

  private _renderTags(tags: string[]): VNode {
    return tags
      ?.filter((tag: string) => {
        return tag != null && tag !== ""
      })
      ?.map((tag: string) => {
        return (
          <calcite-combobox-item
            value={tag}
            text-label={tag}
            scale="m"
            selected="true"
          >
          </calcite-combobox-item>
        )
      });
  }


  /** Find <input/> in combobox and setup blur event */
  private _comboboxHandler(combobox: HTMLCalciteComboboxElement) {

    combobox.addEventListener("calciteComboboxFilterChange", this._handleComboboxOnInputChg.bind(this));
    combobox.addEventListener("blur", this._handleComboboxOnInputChg.bind(this));

    this._recursiveFindGrandChildren(combobox);
  }

  /** Continue calling until grandchildren are found */
  private _recursiveFindGrandChildren(combobox: HTMLCalciteComboboxElement) {
    this._findGrandChildren(combobox);
    if (this._comboboxGrandChildren == null) {
      setTimeout(() => {
        this._recursiveFindGrandChildren(combobox);
      }, 100);
    } else { // grandchildren found - perform initial call
      this._handleComboboxOnInputChg();
    }
  }

  private _FIRST_LEVEL_DIV_CLASS = "wrapper";
  /** Finding combobox grandchildren to get an accurate amount of the chips and their values */
  private _findGrandChildren(combobox: HTMLCalciteComboboxElement) {
    const children = combobox.shadowRoot.children;
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (child.className === this._FIRST_LEVEL_DIV_CLASS) {
        this._comboboxGrandChildren = child.children;
      }
    }
  }

  private _handleComboboxOnInputChg(onChgEvent?: Event) {
    // clear current tags
    this._appTags = [];

    // collect tags from the chips
    for (let j = 0; j < this._comboboxGrandChildren.length; j++) {
      const grandChild = this._comboboxGrandChildren[j];
      if (grandChild.className === "chip") {
        const chipVal = (grandChild as HTMLCalciteChipElement).value;
        if (chipVal != null && chipVal !== "") {
          this._appTags.push(chipVal);
        }
      }
    }

    // take any extraVals that were entered into <input/> but user didn't press Enter to turn into chip
    const extraVal = (onChgEvent as any)?.detail?.text;
    if (extraVal != null && extraVal !== "") {
      // case 1: user is typing in text
      this._appTags.push(extraVal);
      this._currentExtraVal = extraVal;
    } else if (extraVal === "") {
      // case 2: user typed in text and hit enter
      this._currentExtraVal = extraVal;
    } else if (extraVal == null && this._currentExtraVal != null && this._currentExtraVal !== "") {
      // case 3: blur occured and there exists text in the input
      this._appTags.push(this._currentExtraVal);
    }

    this._handleTagValidation();
  }

  private _handleTagValidation() {
    this._isTagsValid = true;
    this._determineFormValidity();
    this.scheduleRender();
  }

  private _renderFolderInput(): VNode {
    const { publishForm } = this.appMessages;
    return (
      <calcite-label
      >
        {publishForm.folder}
        <calcite-select
          scale="m"
          width="auto"
          afterCreate={(calciteSelect: HTMLCalciteSelectElement) => {
            calciteSelect.addEventListener("calciteSelectChange", this._handleFolderChange.bind(this))
          }}
        >
          {this.publishAppVM?.folders?.map((folder: UserFolder) => {
            return (
              <calcite-option
                label={folder.title}
                value={folder.id}
                selected={this.mode === "newApp"
                  ? folder.id === AGOL_USER_ROOT_FOLDER_KEY
                  : folder.id === this.existingAppItem.ownerFolder
                }
              >{folder.title}</calcite-option>
            );
          })}
        </calcite-select>
      </calcite-label>
    )
  }

  private async _handleFolderChange(event: Event) {
    this._folderId = (event.target as any).selectedOption.value;

    if (this._appTitle == null || this._appTitle === "") { // if title is not defined yet, then folder change doesn't matter
      return;
    }

    if (await this.publishAppVM.doesTitleExist(this._appTitle, this._folderId)) {
      this._showInvalidTitleAlreadyExists(this._appTitle);
    } else {
      this._showValidTitle();
    }
  }

  private _showValidTitle() {
    this._isTitleValid = true;
    this._determineFormValidity();
    this.scheduleRender();
  }

  private _showInvalidTitleAlreadyExists(appTitle: string) {
    this._isTitleValid = false;
    this._titleInvalidMsg =
      `"${appTitle}" ${this.appMessages.publishForm.titleValidationExists} "${this.publishAppVM.getFolderName(this._folderId)}"`;
    this._determineFormValidity();
    this.scheduleRender();
  }

  private _destroyPublishOverlay() {
    this.sendFocusOnClose?.();
    if (this?.updateFunction != null) {
      this.updateFunction();
    }
    this.destroy();
    this.scheduleRender();
  }

  private _formatAGOLTags(): string {
    return this._appTags.join(",");
  }

}
